import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Brain, Workflow, Code, Target, Zap, Search, Globe, Mail, MapPin, Phone, Coffee, ArrowRight } from 'lucide-react';
import { services, testimonials, blogs, valueBullets } from '../mock';
import precisionInCode from '../assets/precision-in-code.jpg';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { toast } from 'sonner';

const iconMap = {
  'brain-circuit': Brain,
  'workflow': Workflow,
  'code': Code,
  'target': Target,
  'zap': Zap,
  'search': Search,
  'brain': Brain,
  'globe': Globe
};

const Home = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const vantaRef = useRef(null);
  const [vantaEffect, setVantaEffect] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: '' });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name || formData.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setIsSubmitting(true);

    try {
      const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8001').replace('localhost', '127.0.0.1');
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Message sent successfully! We\'ll get back to you soon.');
        setFormData({ name: '', email: '', company: '', message: '' });
        setFormErrors({});
      } else {
        throw new Error(data.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error(`${error.message || 'Failed to send message. Please try again.'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!vantaEffect) {
      const effect = window.VANTA.WAVES({
        el: vantaRef.current,
        mouseControls: true,
        touchControls: true,
        gyroControls: false,
        minHeight: 200.00,
        minWidth: 200.00,
        scale: 1.00,
        scaleMobile: 1.00,
        color: 0x3B82F6,
        shininess: 60,
        waveHeight: 15,
        waveSpeed: 1.0,
        zoom: 0.90
      });
      setVantaEffect(effect);
    }

    return () => {
      if (vantaEffect) vantaEffect.destroy();
    };
  }, [vantaEffect]);

  // Handle scroll to contact when navigating from other pages
  useEffect(() => {
    if (location.state?.scrollTo === 'contact') {
      setTimeout(() => {
        const contactSection = document.getElementById('contact');
        if (contactSection) {
          const navbarHeight = 80;
          const elementPosition = contactSection.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - navbarHeight - 20;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [location]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
            entry.target.classList.remove('opacity-0');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.scroll-reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="bg-gradient-to-b from-gray-50 via-white to-gray-50 text-gray-900 overflow-hidden">
      <Navbar />

      {/* Hero Section */}
      <section ref={vantaRef} id="home" className="min-h-screen flex items-center justify-center relative px-6 lg:px-12" style={{ position: 'relative', backgroundColor: '#f0f9ff' }}>
        <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-blue-50/30 to-white/60 pointer-events-none" style={{ zIndex: 1 }}></div>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500 to-transparent animate-pulse opacity-50" style={{ zIndex: 2 }}></div>

        <div className="max-w-7xl mx-auto text-center relative" style={{ zIndex: 10 }}>
          <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-light tracking-tight mb-8 leading-[1.1] text-gray-900 animate-fade-in" style={{ fontWeight: 300 }}>
            Transform Problems into
            <br />
            <span className="text-blue-600">Intelligent Solutions</span>
          </h1>

          <p className="text-lg md:text-xl text-gray-700 max-w-4xl mx-auto mb-12 leading-relaxed font-light animate-fade-in" style={{ animationDelay: '200ms' }}>
            At ThinkVerse.AI, we bridge human creativity and machine intelligence to craft adaptive, ethical, and scalable AI solutions. Our systems don't just automate, they understand, evolve, and empower.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 animate-fade-in" style={{ animationDelay: '400ms' }}>
            {/* Try Custom RAG Demo Button */}
            <div className="relative inline-block group">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl blur opacity-60 group-hover:opacity-100 transition-opacity duration-500"></div>
              <button
                onClick={() => window.location.href = '/custom-rag-demo'}
                className="relative bg-gradient-to-r from-blue-600 to-purple-600 hover:from-purple-600 hover:to-blue-600 text-white px-8 py-4 rounded-xl transition-all duration-300 shadow-[0_0_40px_rgba(59,130,246,0.5)] hover:shadow-[0_0_60px_rgba(147,51,234,0.6)] hover:scale-105 font-semibold text-lg flex items-center gap-2 border border-white/10"
              >
                <span className="relative z-10 flex items-center text-white">
                  <Brain size={22} className="mr-2 animate-pulse text-white" /> Try Your Own ChatGPT
                </span>
                <div className="absolute inset-0 rounded-xl overflow-hidden">
                  <div className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
                </div>
              </button>
            </div>

            {/* Explore Services Button */}
            <div className="relative inline-block group">
              <div className="absolute -inset-1 bg-gradient-to-r from-slate-400 via-slate-500 to-slate-400 rounded-xl blur opacity-40 group-hover:opacity-70 transition-opacity duration-500"></div>
              <button
                onClick={() => {
                  const servicesSection = document.getElementById('services');
                  if (servicesSection) {
                    const navbarHeight = 80;
                    const elementPosition = servicesSection.getBoundingClientRect().top + window.pageYOffset;
                    const offsetPosition = elementPosition - navbarHeight - 20;
                    window.scrollTo({
                      top: offsetPosition,
                      behavior: 'smooth'
                    });
                  }
                }}
                className="relative bg-white text-slate-800 border border-slate-200 px-8 py-4 rounded-xl transition-all duration-300 shadow-sm hover:shadow-xl hover:scale-105 font-semibold text-lg"
              >
                <span className="relative z-10 text-slate-800">Explore Our Services</span>
                <div className="absolute inset-0 rounded-xl overflow-hidden">
                  <div className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 bg-gradient-to-r from-transparent via-slate-100 to-transparent"></div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-32 px-6 lg:px-12 relative bg-white overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-100/30 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-sky-100/30 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>

        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-20 items-start">
            <div className="scroll-reveal opacity-0">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Frame Smarter Strategies.
                <br />
                <span className="text-blue-600">Build Intelligent Solutions.</span>
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                From AI strategy to full-stack development, ThinkVerse.AI transforms your digital ecosystem with innovation, scalability, and intelligence.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              {services.map((service, index) => {
                const Icon = iconMap[service.icon];
                return (
                  <Card
                    key={service.id}
                    className="bg-white border border-gray-200 hover:border-blue-500/50 backdrop-blur-sm transition-all duration-500 hover:scale-105 hover:shadow-2xl scroll-reveal opacity-0 group relative overflow-hidden"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-sky-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                    <CardContent className="p-6 relative">
                      <div className="relative mb-4">
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-sky-500 rounded-full blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-500"></div>
                        <div className="relative text-blue-600 group-hover:scale-110 transition-transform duration-300">
                          <Icon size={40} />
                        </div>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-300">{service.title}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{service.description}</p>

                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-blue-500 to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 px-6 lg:px-12 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgb(59 130 246) 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>

        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div className="scroll-reveal opacity-0">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
                Precision in Every
                <br />
                <span className="text-blue-600">Line of Code.</span>
              </h2>
              <p className="text-2xl font-light text-gray-700 mb-6">Crafting Intelligence, One Line at a Time.</p>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                At ThinkVerse.AI, we merge deep AI expertise with creative engineering to deliver future-ready systems. Each solution we build is guided by clarity, precision, and purpose — transforming complex problems into elegant, scalable products.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {valueBullets.map((bullet, index) => {
                  const Icon = iconMap[bullet.icon];
                  return (
                    <div key={index} className="flex items-center space-x-3">
                      <Icon size={20} className="text-blue-600" />
                      <span className="text-gray-700">{bullet.text}</span>
                    </div>
                  );
                })}
              </div>

              <div className="relative inline-block group">
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-sky-500 rounded-xl blur opacity-30 group-hover:opacity-60 transition-opacity duration-500"></div>
                <Button
                  size="lg"
                  className="relative bg-gradient-to-r from-blue-500 to-sky-500 hover:from-sky-500 hover:to-blue-500 text-white px-8 py-6 shadow-lg hover:shadow-2xl transition-all duration-300"
                >
                  <span className="relative z-10 flex items-center">
                    Let's Innovate Together <ArrowRight className="ml-2" size={20} />
                  </span>
                  <div className="absolute inset-0 rounded-xl overflow-hidden">
                    <div className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
                  </div>
                </Button>
              </div>
            </div>

            <div className="scroll-reveal opacity-0 flex items-center justify-center">
              <div className="relative w-full max-w-xl aspect-[16/10]">
                <img
                  src={precisionInCode}
                  alt="AI Development cover"
                  className="relative w-full h-full object-contain rounded-2xl shadow-2xl bg-transparent transform transition duration-500 ease-out hover:scale-105"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-32 px-6 lg:px-12 bg-white relative overflow-hidden">
        <div className="absolute top-1/2 left-0 w-96 h-96 bg-blue-100/20 rounded-full blur-3xl -translate-y-1/2 -translate-x-1/2"></div>
        <div className="absolute top-1/2 right-0 w-96 h-96 bg-sky-100/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>

        <div className="max-w-7xl mx-auto relative">
          <div className="text-center mb-20 scroll-reveal opacity-0">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">What Our Partners Say</h2>
            <p className="text-gray-600 text-lg">Real impact. Real results.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-10">
            <div className="relative group scroll-reveal opacity-0">
              <div className="absolute -inset-1 bg-gradient-to-br from-blue-500 to-sky-500 rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-500"></div>
              <Card className="relative bg-gradient-to-br from-blue-600 to-blue-500 border-none shadow-2xl">
                <CardContent className="p-8">
                  <div className="mb-6">
                    <svg className="w-12 h-12 text-white/30" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                    </svg>
                  </div>
                  <p className="text-xl leading-relaxed mb-6 text-white">
                    {testimonials[0].text}
                  </p>
                  <div>
                    <p className="font-semibold text-white">{testimonials[0].author}</p>
                    <p className="text-white/90 text-sm">{testimonials[0].position}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-4 scroll-reveal opacity-0">
              {testimonials[1].messages.map((msg, idx) => (
                <div
                  key={msg.id}
                  className="relative group"
                  style={{ animationDelay: `${idx * 100}ms` }}
                >
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500/20 to-sky-500/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative bg-white backdrop-blur-sm border border-gray-200 rounded-2xl p-6 hover:border-blue-500/50 transition-all duration-300 hover:translate-x-2 shadow-sm hover:shadow-lg">
                    <p className="text-gray-800">{msg.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section id="blog" className="py-32 px-6 lg:px-12 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgb(59 130 246) 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>

        <div className="max-w-7xl mx-auto relative">
          <div className="text-center mb-20 scroll-reveal opacity-0">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Insights & Ideas from the ThinkVerse Lab</h2>
            <p className="text-gray-600 text-lg">Discover the latest in AI innovation, automation, and design intelligence.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            {blogs.map((blog, index) => (
              <div key={blog.id} className="relative group scroll-reveal opacity-0 flex" style={{ animationDelay: `${index * 100}ms` }}>
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/20 to-sky-500/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur"></div>

                <Card className="relative bg-white border border-gray-200 hover:border-blue-500/50 backdrop-blur-sm transition-all duration-500 hover:scale-105 hover:shadow-2xl overflow-hidden flex flex-col w-full">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-sky-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>

                  <CardContent className="p-6 flex flex-col flex-1">
                    <div className="mb-4">
                      <span className="inline-block px-3 py-1 text-xs font-semibold bg-gradient-to-r from-blue-500/10 to-sky-500/10 text-blue-600 rounded-full border border-blue-500/20">
                        {blog.category}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">{blog.title}</h3>
                    <p className="text-gray-600 text-sm mb-4 leading-relaxed flex-1">{blog.excerpt}</p>
                    <div className="flex items-center justify-between mt-auto">
                      <span className="text-gray-400 text-xs">{blog.date}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 group/btn"
                      >
                        Read More <ArrowRight className="ml-1 group-hover/btn:translate-x-1 transition-transform" size={16} />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 px-6 lg:px-12 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 w-[600px] h-[600px] bg-gradient-to-r from-blue-100/30 to-sky-100/30 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>

        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-20">
            <div className="scroll-reveal opacity-0">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                ThinkVerse.AI is Ready to
                <br />
                <span className="text-blue-600">Help You Grow.</span>
              </h2>
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                Let's discuss how AI can accelerate your business — from automation to innovation.
              </p>

              <div className="space-y-6">
                <div className="flex items-start space-x-4 group">
                  <div className="relative">
                    <div className="absolute inset-0 bg-blue-500/20 rounded-full blur group-hover:blur-lg transition-all duration-300"></div>
                    <div className="relative bg-gradient-to-r from-blue-500 to-sky-500 p-3 rounded-full">
                      <MapPin className="text-white" size={20} />
                    </div>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Location</p>
                    <p className="text-gray-600 text-sm">H No. 43 Sukhsagar Phase 4, Bhanpur</p>
                    <p className="text-gray-600 text-sm">Bhopal, Madhya Pradesh 462022, India</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 group">
                  <div className="relative">
                    <div className="absolute inset-0 bg-blue-500/20 rounded-full blur group-hover:blur-lg transition-all duration-300"></div>
                    <div className="relative bg-gradient-to-r from-blue-500 to-sky-500 p-3 rounded-full">
                      <Mail className="text-white" size={20} />
                    </div>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Email</p>
                    <a href="mailto:info@thethinkverse.ai" className="text-gray-600 hover:text-blue-600 transition-colors">
                      info@thethinkverse.ai
                    </a>
                  </div>
                </div>
                <div className="flex items-start space-x-4 group">
                  <div className="relative">
                    <div className="absolute inset-0 bg-blue-500/20 rounded-full blur group-hover:blur-lg transition-all duration-300"></div>
                    <div className="relative bg-gradient-to-r from-blue-500 to-sky-500 p-3 rounded-full">
                      <Phone className="text-white" size={20} />
                    </div>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Phone</p>
                    <a href="tel:+918305940684" className="text-gray-600 hover:text-blue-600 transition-colors">
                      +91 83059 40684
                    </a>
                  </div>
                </div>
                <div className="flex items-start space-x-4 group">
                  <div className="relative">
                    <div className="absolute inset-0 bg-blue-500/20 rounded-full blur group-hover:blur-lg transition-all duration-300"></div>
                    <div className="relative bg-gradient-to-r from-blue-500 to-sky-500 p-3 rounded-full">
                      <Coffee className="text-white" size={20} />
                    </div>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Visit Us</p>
                    <p className="text-gray-600">Drop by for a conversation — coffee's on us.</p>
                  </div>
                </div>
              </div>
            </div>

            <Card className="relative group scroll-reveal opacity-0 bg-white border border-gray-200 backdrop-blur-sm shadow-xl">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/20 to-sky-500/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl -z-10"></div>
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Input
                      type="text"
                      name="name"
                      placeholder="Your Name *"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      disabled={isSubmitting}
                      className={`bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 ${formErrors.name ? 'border-red-500' : ''}`}
                    />
                    {formErrors.name && (
                      <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>
                    )}
                  </div>
                  <div>
                    <Input
                      type="email"
                      name="email"
                      placeholder="Your Email *"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      disabled={isSubmitting}
                      className={`bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 ${formErrors.email ? 'border-red-500' : ''}`}
                    />
                    {formErrors.email && (
                      <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>
                    )}
                  </div>
                  <div>
                    <Input
                      type="text"
                      name="company"
                      placeholder="Company Name (Optional)"
                      value={formData.company}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      className="bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <Textarea
                      name="message"
                      placeholder="Your Message (Optional)"
                      value={formData.message}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      rows={5}
                      className="bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 resize-none"
                    />
                  </div>
                  <div className="relative group/btn">
                    <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 via-sky-500 to-blue-500 rounded-xl blur opacity-30 group-hover/btn:opacity-60 transition-opacity duration-500"></div>
                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="relative w-full bg-gradient-to-r from-blue-500 to-sky-500 hover:from-sky-500 hover:to-blue-500 text-white shadow-lg hover:shadow-2xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sending...
                        </span>
                      ) : (
                        <span className="relative z-10">Send Message</span>
                      )}
                      <div className="absolute inset-0 rounded-xl overflow-hidden">
                        <div className="absolute inset-0 translate-x-[-100%] group-hover/btn:translate-x-[100%] transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
                      </div>
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
